import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { Button, TextField, Box, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import * as Yup from 'yup';
import 'react-toastify/dist/ReactToastify.css';
import useApiCaller from 'hooks/apiCaller';

const CreateDeclarationForm = ({ onTrigger }) => {
  const [courses, setCourses] = useState([]);
  const apiCaller = useApiCaller();

  useEffect(() => {
    const handleGetAllCourses = async () => {
      try {
        const response = await apiCaller.get('/super-user/getAllCourse');
        if (response) {
          setCourses(response?.data?.data?.courses || []);
        }
      } catch (error) {
        console.error(error);
      }
    };
    handleGetAllCourses();
  }, []);

  const validationSchema = Yup.object().shape({
    courseId: Yup.string().required('Declaration Name is required'),
    date: Yup.string().required('Deadline Date is required'),
    templateFile: Yup.mixed().required('Declaration Document is required'),
    empIdsFile: Yup.mixed()
      .nullable()
      .test(
        'has-employees',
        'Please upload an Employee ID file with at least one employee',
        (val) => Array.isArray(val) && val.length > 0
      ),
  });

  const handleFileUpload = (event, setFieldValue) => {
    const file = event.currentTarget.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.readAsBinaryString(file);

    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet);

        if (!jsonData.length) {
          toast.error('The uploaded Excel file is empty. Please check the file and try again.');
          setFieldValue('empIdsFile', null);
          return;
        }
        if (!jsonData[0].hasOwnProperty('EmpID')) {
          toast.error(
            'Excel file must have an "EmpID" column. Please download the template and use the correct format.'
          );
          setFieldValue('empIdsFile', null);
          return;
        }
        setFieldValue('empIdsFile', jsonData);
      } catch (err) {
        toast.error(
          'Failed to read the Excel file. Please ensure it is a valid .xlsx or .csv file.'
        );
        setFieldValue('empIdsFile', null);
      }
    };

    reader.onerror = () => {
      toast.error('Could not read the file. Please try again.');
      setFieldValue('empIdsFile', null);
    };
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const formData = new FormData();
    formData.append('courseId', values.courseId);
    // courseName is the human-readable display name (spaces preserved)
    formData.append('courseName', values.courseName);
    formData.append('date', values.date);

    if (!(values.templateFile instanceof File)) {
      toast.error('Please upload the declaration document before submitting.');
      setSubmitting(false);
      return;
    }
    formData.append('templateFile', values.templateFile);

    if (!Array.isArray(values.empIdsFile) || values.empIdsFile.length === 0) {
      toast.error('Please upload a valid Employee IDs file before submitting.');
      setSubmitting(false);
      return;
    }
    formData.append('empIdsFile', JSON.stringify(values.empIdsFile));

    try {
      const response = await apiCaller.post('/super-user/create-declaration', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (response) {
        toast.success('Declaration Created Successfully!', { theme: 'colored' });
        if (onTrigger) onTrigger(values);
        window.location.reload();
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      const serverMessage = error?.response?.data?.message;
      toast.error(serverMessage || 'Failed to create declaration. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Formik
        initialValues={{
          courseId: '',
          courseName: '',
          date: '',
          templateFile: null,
          empIdsFile: null,
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, isSubmitting }) => (
          <Form>
            {/* Course Name */}
            <Field
              as={TextField}
              name="courseId"
              label="Enter Declaration Name"
              fullWidth
              margin="normal"
              onChange={(event) => {
                const raw = event.target.value;
                setFieldValue('courseName', raw);
                setFieldValue('courseId', raw.replace(/\s+/g, '-'));
              }}
            />
            <ErrorMessage name="courseId" component="div" style={{ color: 'red' }} />

            {/* Date Field */}
            <Field
              as={TextField}
              name="date"
              label="Select Deadline Date"
              type="date"
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split('T')[0] }}
            />
            <ErrorMessage name="date" component="div" style={{ color: 'red' }} />

            {/* Template File Upload */}
            <Box mt={2}>
              <Typography variant="subtitle1">Upload Declaration Document</Typography>
              <input
                type="file"
                name="templateFile"
                accept=".doc,.docx,.pdf,image/*"
                onChange={(event) => setFieldValue('templateFile', event.currentTarget.files[0])}
                style={{ display: 'block', marginTop: '8px' }}
              />
              <ErrorMessage name="templateFile" component="div" style={{ color: 'red' }} />
            </Box>

            {/* Employee IDs Excel Upload */}
            <Box mt={2}>
              <Typography variant="subtitle1">Upload Employee IDs (Excel/CSV)</Typography>
              <input
                type="file"
                name="empIdsFile"
                accept=".csv,.xlsx"
                onChange={(event) => handleFileUpload(event, setFieldValue)}
                style={{ display: 'block', marginTop: '8px' }}
              />
              <Button
                variant="outlined"
                sx={{ mt: 1 }}
                href="/empUploadDeclarationTemplatexlsx.xlsx"
                download="Employee_Template.xlsx"
              >
                Download Emp-Template
              </Button>
              <ErrorMessage name="empIdsFile" component="div" style={{ color: 'red' }} />
            </Box>

            {/* Submit Button */}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={isSubmitting}
              sx={{ mt: 2 }}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </Button>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default CreateDeclarationForm;
